import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { useKonvaImageDimensions, useKonvaStageDims } from 'contexts/ToolDimensionsAndScaleContext';
import Konva from 'konva';
import { BREAK_POINT_FOR_MOBILE_VIEW, STANDARD_IMAGERY_SIZE } from '../../constants';
import { useRefDispatch, useRefs } from 'contexts/RefContext';
import { convertCoordsIntoKonvaCardinateSystem, rotateVectorDeg } from 'components/tool/utils';
import { debounce } from '../../util';
import { setKonvaStagePosition, setStageRotation, setZoomLevel } from 'store/slices/ToolSlice';
import { selectCustomMap } from 'store/selectors/customMapSelector';

export const useKonvaPositioning = () => {
	const stageRef = useRef<Konva.Stage | null>(null);

	const { threeDModelEnabled, fullScreenEnabled, fullViewSidePanel, zoomLevel }
		= useSelector((state: RootState) => state.toolNewPostions.data);
	const { isImageEditingEnabled } = useSelector(selectCustomMap);

	const imgHeight = useSelector((state: RootState) => state.roofData.data.imgHeight);
	const isB2C = useSelector((state: RootState) => !!state.roofData.data.org?.setting?.isB2C);

	const konvaStageDims = useKonvaStageDims();
	const konvaImageDims = useKonvaImageDimensions();
	const { desktopViewWrapRef: divRef, sidePanelDivRef } = useRefs();

	const dispatch = useDispatch();
	const refDispatch = useRefDispatch();

	useLayoutEffect(() => {
		refDispatch({ type: 'ADD_KONVA_REF', payload: { ref: stageRef } });
	}, [refDispatch]);

	const konvaStageDimsRef = useRef(konvaStageDims);
	const konvaImgDimsRef = useRef(konvaImageDims);
	// const isDesktopView = konvaStageDims.width > BREAK_POINT_FOR_MOBILE_VIEW;
	const isDesktopViewRef = useRef(konvaStageDims.width > BREAK_POINT_FOR_MOBILE_VIEW);
	useEffect(() => {
		konvaStageDimsRef.current = konvaStageDims;
		konvaImgDimsRef.current = konvaImageDims;
		isDesktopViewRef.current = konvaStageDims.width > BREAK_POINT_FOR_MOBILE_VIEW;
	}, [konvaStageDims, konvaImageDims]);

	// ------restore pos/scale when 3d is toggled OFF
	// Base behavior matches stable: restore scale from redux, no DOM measurement involved. Position
	// is the one deliberate departure — see the comment below, just above adjustPosition(true).
	const prevThreeDMode = useRef(threeDModelEnabled);
	useEffect(() => {
		// Entering 3D: reset the hidden 2D stage to north and re-centre it right away, with no
		// animation, so it is already in place when 3D is closed — e.g. rotate 40°, open 3D, close
		// 3D: the 2D map is back at north and centred, with no visible transition.
		// The 2D stage stays mounted (only hidden) while 3D is open, so it can be reset here.
		if (!prevThreeDMode.current && stageRef.current && threeDModelEnabled) {
			stageRef.current.rotation(0);
			dispatch(setStageRotation({ rotation: 0 }));
			const position = getCenteredPosition(stageRef.current);
			stageRef.current.position(position);
			dispatch(setKonvaStagePosition({ position }));
		}
		if (prevThreeDMode.current && stageRef.current && !threeDModelEnabled) {
			stageRef.current.scale({ x: zoomLevel, y: zoomLevel });
			stageRef.current.batchDraw();
		}

		prevThreeDMode.current = threeDModelEnabled;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch, threeDModelEnabled, zoomLevel]);

	// --------------------scale------------------
	// reset scale on fullview toggle
	const konvaScaleRef = useRef(1);
	useEffect(() => {
		const isDesktopView = konvaStageDims.width > BREAK_POINT_FOR_MOBILE_VIEW;
		let konvaZoomScale = imgHeight / STANDARD_IMAGERY_SIZE;
		if (fullScreenEnabled) {
			const isImagerySmall = imgHeight < 700;
			konvaZoomScale = isDesktopView
				? isImagerySmall ? 2.5 : 3.5
				: isImagerySmall ? 2 : 2.5;
		}
		// B2C keeps the imagery's aspect ratio, which leaves empty bands on a canvas of a different shape.
		// Zoom in just enough to cover the whole canvas instead; adjustPosition keeps it centred.
		// e.g. canvas 1200x600, image shown at 600x600, default zoom 1 → zoom 2.
		const hasImageDims = konvaImageDims.width > 0 && konvaImageDims.height > 0;
		if (isB2C && !fullScreenEnabled && hasImageDims) {
			const coverZoom = Math.max(
				konvaStageDims.width / konvaImageDims.width,
				konvaStageDims.height / konvaImageDims.height,
			);
			konvaZoomScale = Math.max(konvaZoomScale, coverZoom);
		}
		konvaScaleRef.current = konvaZoomScale;
		dispatch(setZoomLevel({ scaleBy: konvaZoomScale }));
		// This resets position to center (setZoomLevel's own default when no position is passed)
		// on fullscreen toggle, window resize (konvaStageDims.width), and imagery/mode changes
		// (imgHeight) — rotation resets to north right alongside it, same as every other trigger.
		dispatch(setStageRotation({ rotation: 0 }));
	}, [dispatch, fullScreenEnabled, imgHeight, konvaStageDims.width, konvaStageDims.height,
		konvaImageDims.width, konvaImageDims.height, isB2C]);

	// -------- Position---------------------
	// calculate posiotn and sjust stage conditionally through
	// animate(when fullscreen) or direct upate

	// the stage position that centres the image for the stage's current scale and rotation
	function getCenteredPosition(stage: Konva.Stage): Vector2d {
		const { width: stageWidth, height: stageHeight } = konvaStageDimsRef.current;

		// const animate = fullScreenRef.current;

		const konvaZoomScale = fullScreenRef.current ? stage.scaleX() : konvaScaleRef.current;

		let offset = { x: 0, y: 0 };

		if (fullScreenRef.current) {
			offset = offSetToAddToCenterRef.current;
		}
		const canvasCenter = {
			x: stageWidth / 2 + Math.max(offset.x, 0),
			y: stageHeight / 2
		};

		// Rotation-aware: reduces to staging's plain imgW/imgH half-subtraction when
		// stage.rotation() is 0 (rotateVectorDeg(v, 0) === v). Staging never needs this since it
		// has no 2D rotation at all; here it's required for rotate to work — without it, this
		// (which fires on every side-panel toggle and konvaImageDims change) would re-center along
		// the UNROTATED axes and visibly knock a rotated stage back to axis-aligned.
		const localCenter = { x: konvaImgDimsRef.current.width / 2, y: konvaImgDimsRef.current.height / 2 };
		const rotatedOffset = rotateVectorDeg(
			{ x: localCenter.x * konvaZoomScale, y: localCenter.y * konvaZoomScale },
			stage.rotation(),
		);

		return {
			x: canvasCenter.x - rotatedOffset.x,
			y: canvasCenter.y - rotatedOffset.y
		};
	}

	const adjustPosition = useCallback((animate: boolean) => {
		const stage = stageRef.current;
		if (!stage) return;

		const position = getCenteredPosition(stage);

		// if(animate){
		stage.to({
			x: position.x,
			y: position.y,
			duration: 0.5,
			// eslint-disable-next-line no-constant-condition
			easing: true ? Konva.Easings.EaseInOut : Konva.Easings.Linear,
		});
		// }else{
		// 	stage.position({
		// 		x: position.x,
		// 		y: position.y,
		// 		// duration: 0.5,
		// 		// easing: animate ? Konva.Easings.EaseInOut : Konva.Easings.Linear,
		// 	});
		// }

		dispatch(setKonvaStagePosition({position}));
	}, [dispatch]);

	const fullScreenRef = useRef(false);
	useEffect(() => {
		fullScreenRef.current = fullScreenEnabled;
	}, [fullScreenEnabled]);

	//------------- offset -------------
	// need offset only when left side panel(divRef) is toggled
	// in fullview mode in Desktop view;

	const offSetToAddToCenterRef = useRef({ x: 0, y: 0 });
	useLayoutEffect(() => {
		const stage = stageRef.current;
		const div = divRef?.current;

		let offSetToAddToCenterlocl = { x: 0, y: 0 };

		if (!stage || !div || !fullScreenRef.current || prevThreeDMode.current) {
			offSetToAddToCenterRef.current = offSetToAddToCenterlocl;
			return;
		}
		const needsOffset = isDesktopViewRef.current && (fullViewSidePanel);
		if (needsOffset) {
			const divBox = div.getBoundingClientRect();
			const divWidth = divBox.width;
			const divBottom = divBox.bottom;

			offSetToAddToCenterlocl =
				convertCoordsIntoKonvaCardinateSystem({ x: divWidth / 2, y: divBottom }, stage);
		}

		offSetToAddToCenterRef.current = offSetToAddToCenterlocl;
		adjustPosition(true);

	}, [adjustPosition, divRef, fullViewSidePanel]);

	useLayoutEffect(() => {
		const stage = stageRef.current;
		if (!stage) return;

		const isActive = !isDesktopViewRef.current && isImageEditingEnabled;
		const panelWidth = isActive && sidePanelDivRef?.current
			? sidePanelDivRef.current.getBoundingClientRect().width
			: 0;

		const { width: stageWidth, height: stageHeight } = konvaStageDimsRef.current;
		const konvaZoomScale = fullScreenRef.current ? stage.scaleX() : konvaScaleRef.current;

		// Rotation-aware for the same reason as adjustPosition above — this fires on every
		// isImageEditingEnabled toggle and must not undo a rotated stage's position.
		const localCenter = { x: konvaImgDimsRef.current.width / 2, y: konvaImgDimsRef.current.height / 2 };
		const rotatedOffset = rotateVectorDeg(
			{ x: localCenter.x * konvaZoomScale, y: localCenter.y * konvaZoomScale },
			stage.rotation(),
		);

		const position: Vector2d = {
			x: (stageWidth / 2 - panelWidth / 2) - rotatedOffset.x, // shift left by half panel width
			y: stageHeight / 2 - rotatedOffset.y
		};

		stage.to({
			x: position.x,
			y: position.y,
			duration: 0.5,
			easing: Konva.Easings.EaseInOut,
		});

		dispatch(setKonvaStagePosition({ position }));
	}, [dispatch, isImageEditingEnabled, sidePanelDivRef]);


	// ---------------- adjust position whenever screen/img zise chnages-----------------------
	// Resets rotation to north alongside the recenter, same as every other reset trigger —
	// adjustPosition itself stays rotation-aware/non-resetting since it's shared with the
	// panel-offset and isImageEditingEnabled effects above, which must preserve rotation.
	// Rotation is reset first: adjustPosition centres for the stage's current rotation, so
	// centring before the reset leaves a rotated map off-centre (e.g. a height-only resize).
	const debouncedAdjustPosition = useMemo(() =>
		debounce(() => {
			stageRef.current?.rotation(0);
			dispatch(setStageRotation({ rotation: 0 }));
			adjustPosition(false);
		}, 300), [adjustPosition, dispatch]);

	useLayoutEffect(() => {
		debouncedAdjustPosition();
	}, [debouncedAdjustPosition, konvaImageDims]);

	return stageRef;
};
